import styled from "styled-components";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Control,
  FieldValues,
  Path,
  useController,
  useWatch,
} from "react-hook-form";

import { Input } from "@/components/Forms";
import Text from "@/components/Text";
import { TestingCurrencies } from "@/utils/enums";

const BudgetInputsWrapper = styled.div`
  display: flex;
`;

const DisabledInput = styled(Input)`
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  border-left-color: transparent;
  padding-left: 30px;
  color: ${({ theme }) => theme.colors.gray};
`;

const StyledInput = styled(Input)`
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  padding-left: 34px;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const InputHolder = styled.div`
  position: relative;
  width: 100%;
`;

const Currency = styled(Text)`
  position: absolute;
  left: 16px;
  top: 12px;
`;

const renderCurrencySymbol = (currency: TestingCurrencies) => {
  switch (currency) {
    case TestingCurrencies.KRW:
      return "₩";
    case TestingCurrencies.EUR:
      return "€";
    default:
      return "$";
  }
};

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  inputId: string;
  placeholder?: string;
  currency?: TestingCurrencies;
}

const CurrencyField = <T extends FieldValues>({
  control,
  name,
  inputId,
  currency = TestingCurrencies.KRW,
  placeholder = "Enter budget",
}: Props<T>) => {
  const { data: currencyRate } = useQuery(
    [`${currency}_to_usd_currency_rate`],
    () =>
      axios
        .get(
          `https://api.exchangerate.host/convert?from=${currency}&to=USD&amount=100`
        )
        .then((response) => response.data?.info?.rate),
    {
      refetchOnMount: false,
    }
  );

  const value = useWatch({
    name,
    control,
  });

  const [valueInUsd, setValueInUsd] = useState<null | string>(null);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  useEffect(() => {
    if (!currencyRate || !value) {
      return setValueInUsd(null);
    }

    setValueInUsd((value * currencyRate).toFixed(2));
  }, [currencyRate, value]);

  return (
    <BudgetInputsWrapper>
      <InputHolder>
        <StyledInput
          $hasError={!!error}
          id={inputId}
          type="number"
          placeholder={placeholder}
          {...field}
        />
        <Currency $type="captionSemibold">
          {renderCurrencySymbol(currency)}
        </Currency>
      </InputHolder>
      <InputHolder>
        <DisabledInput value={valueInUsd ?? "-"} disabled readOnly />
        <Currency $color="gray" $type="captionSemibold">
          $
        </Currency>
      </InputHolder>
    </BudgetInputsWrapper>
  );
};

export default CurrencyField;
